import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  SelectableInputChange,
  SelectableInputFreeInputResolver,
  SelectableInputOption,
  SelectableInputResolveError,
  SelectableInputValueTransform,
} from './selectable-input.types';
import {
  appendSelectableInputValue,
  findSelectableInputOption,
  mergeSelectableInputOptions,
  resolveSelectableInputCandidate,
  SelectableInputCompareWith,
  selectableInputValuesEqual,
} from './selectable-input-resolver';

@Component({
  standalone: false,
  selector: 'app-selectable-input',
  templateUrl: './selectable-input.component.html',
  styleUrls: ['./selectable-input.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectableInputComponent),
    multi: true,
  }],
})
export class SelectableInputComponent implements ControlValueAccessor, OnChanges {
  @Input() label = '';
  @Input() inputValue = '';
  @Input() selectValue: unknown = null;
  @Input() options: readonly SelectableInputOption[] = [];
  @Input() multiple = false;
  @Input() interface: 'alert' | 'action-sheet' | 'popover' = 'action-sheet';
  @Input() interfaceOptions?: Record<string, unknown>;
  @Input() compareWith?: SelectableInputCompareWith;
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() selectPlaceholder = '';
  @Input() allowFreeInput = true;
  @Input() valueTransform?: SelectableInputValueTransform;
  @Input() resolveFreeInput?: SelectableInputFreeInputResolver;

  @Output() inputValueChange = new EventEmitter<string>();
  @Output() selectValueChange = new EventEmitter<unknown>();
  @Output() selectionChange = new EventEmitter<SelectableInputChange>();
  @Output() resolveError = new EventEmitter<SelectableInputResolveError>();

  internalOptions: readonly SelectableInputOption[] = [];
  isResolving = false;
  readonly emptyInterfaceOptions: Readonly<Record<string, never>> = Object.freeze({});

  private onModelChange: (value: string) => void = () => undefined;
  private onModelTouched: () => void = () => undefined;
  private resolvedOptions: SelectableInputOption[] = [];
  private lastResolveAttemptValue?: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['compareWith']) this.syncInternalOptions();
  }

  writeValue(value: unknown): void {
    this.inputValue = this.toInputString(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onModelTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onInputChanged(value: string | null | undefined): void {
    if (!this.allowFreeInput || this.disabled) return;
    this.lastResolveAttemptValue = undefined;
    this.updateInputValue(value ?? '', 'free-input');
  }

  onInputBlur(): void {
    this.onModelTouched();
    void this.resolveCurrentFreeInput();
  }

  onInputEnter(): void {
    void this.resolveCurrentFreeInput();
  }

  onSelectChanged(value: unknown): void {
    this.selectValue = value;
    const selectedOptions = this.selectedOptions(value);
    const inputValue = this.valueTransform
      ? this.valueTransform(value, this.multiple ? selectedOptions : selectedOptions[0])
      : this.defaultTransform(value, selectedOptions);
    this.selectValueChange.emit(value);
    this.updateInputValue(inputValue, 'select');
  }

  async resolveCurrentFreeInput(): Promise<void> {
    const resolver = this.resolveFreeInput;
    const candidate = this.inputValue.trim();
    if (!resolver || !this.allowFreeInput || this.disabled || this.isResolving || !candidate) return;
    if (candidate === this.lastResolveAttemptValue) return;
    this.lastResolveAttemptValue = candidate;

    const existing = findSelectableInputOption(this.internalOptions, candidate, this.compareWith);
    if (existing) {
      this.applyResolvedOption(existing);
      this.lastResolveAttemptValue = this.inputValue.trim();
      return;
    }

    this.isResolving = true;
    try {
      const resolved = await resolveSelectableInputCandidate(candidate, {
        options: [...this.internalOptions],
        selectValue: this.selectValue,
        multiple: this.multiple,
      }, resolver, this.compareWith);
      if (!resolved) return;
      const adopted = resolved.option;
      if (resolved.isNew) {
        this.resolvedOptions = [...this.resolvedOptions, adopted];
        this.syncInternalOptions();
      }
      this.applyResolvedOption(adopted);
      this.lastResolveAttemptValue = this.inputValue.trim();
    } catch (error) {
      this.resolveError.emit({ inputValue: candidate, error });
      // 一時的なAPI失敗後、同じ文字列のまま再試行できるようにする。
      this.lastResolveAttemptValue = undefined;
    } finally {
      this.isResolving = false;
    }
  }

  private updateInputValue(value: string, source: SelectableInputChange['source']): void {
    this.inputValue = value;
    this.inputValueChange.emit(value);
    this.selectionChange.emit({ inputValue: value, selectValue: this.selectValue, source });
    this.onModelChange(value);
  }

  private selectedOptions(value: unknown): SelectableInputOption[] {
    const values = this.multiple ? (Array.isArray(value) ? value : []) : [value];
    return this.internalOptions.filter(option => values.some(current => this.valuesEqual(option.value, current)));
  }

  private applyResolvedOption(option: SelectableInputOption): void {
    const nextSelectValue = this.multiple
      ? this.appendMultipleValue(option.value)
      : option.value;
    this.selectValue = nextSelectValue;
    const selectedOptions = this.selectedOptions(nextSelectValue);
    const inputValue = this.valueTransform
      ? this.valueTransform(nextSelectValue, this.multiple ? selectedOptions : option)
      : this.defaultTransform(nextSelectValue, selectedOptions);
    this.selectValueChange.emit(nextSelectValue);
    this.updateInputValue(inputValue, 'resolved');
  }

  private appendMultipleValue(value: unknown): unknown[] {
    return appendSelectableInputValue(this.selectValue, value, this.compareWith);
  }

  private syncInternalOptions(): void {
    this.internalOptions = mergeSelectableInputOptions(this.options, this.resolvedOptions, this.compareWith);
  }

  private valuesEqual(left: unknown, right: unknown): boolean {
    return selectableInputValuesEqual(left, right, this.compareWith);
  }

  private defaultTransform(value: unknown, selectedOptions: SelectableInputOption[]): string {
    if (selectedOptions.length) return selectedOptions.map(option => option.label).join(', ');
    if (Array.isArray(value)) return value.map(item => this.toInputString(item)).join(', ');
    return this.toInputString(value);
  }

  private toInputString(value: unknown): string {
    if (value == null) return '';
    return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  }
}
